import { ImagesHolder } from "../../image_loader/images_holder.js";
import { ImgID } from "../../image_loader/img_id.js";
import { CornerSizes, NineSliceDrawer } from "../../image_loader/nine_slicer/nine_slice_drawer.js";
import { applyNineSliceBorderImage, nineSliceBorderImage, styleAbsoluteLayer } from "../dom_backend/dom_nine_slice.js";
import { snapToDevicePx } from "../dom_backend/dom_backend.js";
import { UIElement } from "./ui_element.js";
import { TransformOptions } from "./ui_element.js";

export class UINineSliceElement extends UIElement
{
  private img_id: ImgID;
  private ns_key: string;
  private corner_size?: CornerSizes;
  private flip_x = false;
  private square_right = false;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    img_id: ImgID,
    ns_key: string,
    corner_size?: CornerSizes,
    options?: TransformOptions,
    is_scalable_on_click = false,
    square_right = false,
  )
  {
    super( ctx, x, y, width, height, false, is_scalable_on_click, options );
    this.img_id = img_id;
    this.ns_key = ns_key;
    this.corner_size = corner_size;
    this.square_right = square_right;
  }

  public setImgId( img_id: ImgID, ns_key?: string ): void
  {
    this.img_id = img_id;
    if ( ns_key !== undefined )
      this.ns_key = ns_key;
  }

  public setFlipX( value: boolean ): void
  {
    this.flip_x = value;
    this.setShouldBeRendered();
  }

  public name(): string
  {
    return "UINineSliceElement";
  }

  private dom_last_key: string = "";

  protected override syncDomContent( _node: HTMLElement ): boolean
  {
    if ( this.square_right )
      return false;

    const spec = nineSliceBorderImage( this.img_id, this.ns_key, this.rect.getW(), this.rect.getH(), this.corner_size );
    if ( !spec )
      return false;

    if ( spec.key === this.dom_last_key )
      return true;
    this.dom_last_key = spec.key;

    applyNineSliceBorderImage( this.getDomSkin().style, spec );
    if ( this.seam_backing )
      this.syncSeamStrips( spec.url );
    return true;
  }

  private seam_backing = false;
  private seam_strips: HTMLDivElement[] = [];

  /**
   * DOM mode: puts a strip of the stretched middle under each vertical seam between the side caps
   * and the middle. The browser draws the border-image pieces separately, and on a fractional
   * device-pixel edge the seam ends up semi-transparent, so whatever lies under the element shows
   * through as a thin line. Only for art sliced horizontally (the middle spans the full height).
   */
  public setSeamBacking( value: boolean ): void
  {
    if ( value === this.seam_backing ) return;
    this.seam_backing = value;
    this.dom_last_key = "";
    if ( !value )
    {
      for ( const strip of this.seam_strips ) strip.remove();
      this.seam_strips = [];
    }
    this.setShouldBeRendered();
  }

  private syncSeamStrips( url: string ): void
  {
    const caps = NineSliceDrawer.computeCaps( this.ns_key, this.rect.getW(), this.rect.getH(), this.corner_size );
    if ( !caps || caps.rule.x2 <= caps.rule.x1 ) return;
    const rule = caps.rule;
    const w = this.rect.getW();
    // 2 px wide, centred on the seam; shows exactly the middle columns [x1, x2) stretched.
    const strip_w = 2;
    const k = strip_w / ( rule.x2 - rule.x1 );
    const seams = [snapToDevicePx( caps.tl_w ), w - snapToDevicePx( caps.tr_w )];
    const skin = this.getDomSkin();
    const node = this.getDomNode();
    seams.forEach( ( seam_x, i ) =>
    {
      let strip = this.seam_strips[i];
      if ( !strip )
      {
        strip = document.createElement( "div" );
        styleAbsoluteLayer( strip );
        strip.style.height = "100%";
        strip.style.backgroundRepeat = "no-repeat";
        this.seam_strips[i] = strip;
      }
      // Before the skin, so the border-image paints over it.
      if ( strip.parentNode !== node )
        node.insertBefore( strip, skin );
      const s = strip.style;
      s.left = `${seam_x - strip_w / 2}px`;
      s.width = `${strip_w}px`;
      s.backgroundImage = `url("${url}")`;
      s.backgroundSize = `${rule.image_width * k}px 100%`;
      s.backgroundPosition = `${-rule.x1 * k}px 0`;
    } );
  }

  protected override domStaticTransform(): string
  {
    const base = super.domStaticTransform();
    if ( !this.flip_x ) return base;
    return base ? `${base} scaleX(-1)` : "scaleX(-1)";
  }

  public getX(): number
  {
    return this.rect.getX();
  }

  public getY(): number
  {
    return this.rect.getY();
  }

  public setHeight( height: number ): void
  {
    this.rect.resize( this.rect.getW(), height );
    this.setShouldBeRendered();
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();

    const x = -this.rect.getW2();
    const y = -this.rect.getH2();
    const w = this.rect.getW();
    const h = this.rect.getH();

    this.applyTransform();

    if ( this.flip_x )
      this.ctx.scale( -1, 1 );

    if ( this.square_right )
    {
      // Draw the regular right cap beyond the visible bounds and clip it off.
      // This preserves the asset's rounded left end and middle styling while
      // producing a clean vertical edge on the right.
      this.ctx.beginPath();
      this.ctx.rect( x, y, w, h );
      this.ctx.clip();
    }

    if ( !this.isDomNative() )
      NineSliceDrawer.drawNineSliceImage(
        this.ctx,
        ImagesHolder.getImage( this.img_id ),
        this.ns_key,
        x,
        y,
        this.square_right ? w + h : w,
        h,
        this.corner_size
      );

    this.finishRender();
  }
}
